import { useState } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import { X, Copy, Share2, Check } from 'lucide-react';
import { haptic } from '../../utils/haptics';
import { shareText } from '../../utils/nativeShare';
import { DOWNLOAD_URL, IS_PRODUCTION, TESTFLIGHT_URL } from '../../constants/links';

interface QRModalProps {
  handle: string;
  name: string;
  open: boolean;
  onClose: () => void;
}

export function QRModal({ handle, name, open, onClose }: QRModalProps) {
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const addUrl = `https://rostandfound.app/add/${handle}`;

  const handleCopy = async () => {
    haptic();
    await navigator.clipboard.writeText(addUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleShare = async () => {
    haptic();
    const text = IS_PRODUCTION
      ? `Add me on Rost & Found!\n\nDownload: ${DOWNLOAD_URL}`
      : `Add me on Rost & Found!\n\nJoin the beta: ${TESTFLIGHT_URL}`;
    const result = await shareText({ title: 'Rost & Found', text, url: addUrl });
    if (result === 'copied') {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-slate-800 rounded-2xl p-6 mx-6 max-w-sm w-full text-center border border-slate-700/50"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-end mb-2">
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300">
            <X size={18} />
          </button>
        </div>
        <div className="bg-white rounded-xl p-4 inline-block mx-auto">
          <QRCodeSVG
            value={addUrl}
            size={180}
            level="M"
            fgColor="#334155"
          />
        </div>
        <p className="text-lg font-bold text-slate-100 mt-4">{name}</p>
        <p className="text-sm text-accent font-mono">@{handle}</p>
        <p className="text-xs text-slate-500 mt-2">
          Scan to add as friend on Rost & Found
        </p>

        {/* Share / Copy buttons */}
        <div className="flex gap-2 mt-4">
          <button
            onClick={handleShare}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-accent text-white text-sm font-medium active:bg-accent-dark active:scale-[0.98] transition-all"
          >
            <Share2 size={14} />
            Share
          </button>
          <button
            onClick={handleCopy}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-slate-300 text-sm font-medium active:bg-slate-600 active:scale-[0.98] transition-all"
          >
            {copied ? <Check size={14} className="text-sage" /> : <Copy size={14} />}
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        </div>
      </div>
    </div>,
    document.getElementById('app-shell') || document.body,
  );
}
